import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import type { AdminGalleryAlbum, AdminGalleryPhoto } from "@tih/shared";
import { GalleryPhotosUploadDropzone } from "./GalleryPhotosUploadDropzone";

type Props = {
  open: boolean;
  onClose: () => void;
  album: AdminGalleryAlbum;
  photos: AdminGalleryPhoto[];
  photosLoading: boolean;
  onUpdate: (patch: { coverUrl?: string | null }) => void;
  onRemovePhoto: (photoId: number) => void;
  onError: (message: string | null) => void;
};

function PhotoPreviewModal({
  photo,
  onClose,
}: {
  photo: AdminGalleryPhoto;
  onClose: () => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="admin-gallery-photo-preview-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="admin-gallery-photo-preview"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Photo preview"
      >
        <button
          type="button"
          className="admin-gallery-photo-preview-close"
          onClick={onClose}
          aria-label="Close preview"
        >
          ×
        </button>
        <img
          src={photo.largeUrl ?? photo.url}
          alt={photo.caption ?? "Gallery photo"}
        />
        {photo.caption && <p className="admin-gallery-photo-preview-caption">{photo.caption}</p>}
      </div>
    </div>,
    document.body,
  );
}

export function GalleryPhotosModal({
  open,
  onClose,
  album,
  photos,
  photosLoading,
  onUpdate,
  onRemovePhoto,
  onError,
}: Props) {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [previewPhoto, setPreviewPhoto] = useState<AdminGalleryPhoto | null>(null);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (previewPhoto) setPreviewPhoto(null);
        else onClose();
      }
    }
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, previewPhoto]);

  useEffect(() => {
    if (!open) {
      setSelectedId(null);
      setPreviewPhoto(null);
    }
  }, [open]);

  function handleSetCover() {
    const photo = photos.find((p) => p.id === selectedId);
    if (!photo) return;
    onUpdate({ coverUrl: photo.url });
  }

  if (!open) return null;

  const selectedPhoto = photos.find((p) => p.id === selectedId) ?? null;
  const coverUrl = album.coverUrl ?? null;

  return createPortal(
    <>
      <div className="admin-gallery-modal-backdrop" onClick={onClose} role="presentation">
        <div
          className="admin-gallery-modal admin-gallery-photos-modal"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-gallery-photos-title"
        >
          <header className="admin-gallery-modal-header">
            <div>
              <h3 id="admin-gallery-photos-title">Album photos</h3>
              <p className="admin-muted admin-gallery-photos-sub">
                {photosLoading
                  ? "Loading…"
                  : `${photos.length} ${photos.length === 1 ? "photo" : "photos"}`}
              </p>
            </div>
            <button
              type="button"
              className="admin-gallery-modal-close"
              onClick={onClose}
              aria-label="Close"
            >
              ×
            </button>
          </header>

          <GalleryPhotosUploadDropzone album={album} onError={onError} />

          {photos.length > 0 && (
            <div className="admin-gallery-photo-toolbar">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                disabled={!selectedPhoto}
                onClick={handleSetCover}
              >
                Set cover
              </button>
              {selectedPhoto && coverUrl === selectedPhoto.url && (
                <span className="admin-gallery-photo-toolbar-hint">Selected photo is the album cover</span>
              )}
              {!selectedPhoto && (
                <span className="admin-gallery-photo-toolbar-hint">
                  Click a photo to select it, then set cover. Double-click to preview.
                </span>
              )}
            </div>
          )}

          {photosLoading ? (
            <p className="admin-muted">Loading photos…</p>
          ) : photos.length === 0 ? (
            <p className="admin-muted">No photos yet. Upload images or a zip to get started.</p>
          ) : (
            <div className="admin-gallery-photo-grid admin-gallery-photo-grid--modal">
              {photos.map((p) => {
                const isSelected = selectedId === p.id;
                const isCover = coverUrl != null && coverUrl === p.url;
                return (
                  <figure
                    key={p.id}
                    className={`admin-gallery-photo-tile${isSelected ? " admin-gallery-photo-tile--selected" : ""}${isCover ? " admin-gallery-photo-tile--cover" : ""}`}
                  >
                    <button
                      type="button"
                      className="admin-gallery-photo-tile-btn"
                      onClick={() => setSelectedId(p.id)}
                      onDoubleClick={() => setPreviewPhoto(p)}
                      aria-label={p.caption ?? `Photo ${p.id}`}
                      aria-pressed={isSelected}
                    >
                      <img src={p.thumbUrl ?? p.url} alt="" loading="lazy" decoding="async" />
                      {isCover && <span className="admin-gallery-photo-cover-badge">Cover</span>}
                    </button>
                    <button
                      type="button"
                      className="admin-gallery-photo-delete"
                      aria-label="Remove photo"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (selectedId === p.id) setSelectedId(null);
                        onRemovePhoto(p.id);
                      }}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path
                          fill="currentColor"
                          d="M9 3h6a1 1 0 0 1 1 1v1h4a1 1 0 1 1 0 2h-1v13a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7H4a1 1 0 1 1 0-2h4V4a1 1 0 0 1 1-1Zm2 2v0h2V5h-2Zm-1 4a1 1 0 1 1 2 0v9a1 1 0 1 1-2 0V9Zm4 0a1 1 0 1 1 2 0v9a1 1 0 1 1-2 0V9ZM8 7v13h8V7H8Z"
                        />
                      </svg>
                    </button>
                  </figure>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {previewPhoto && (
        <PhotoPreviewModal photo={previewPhoto} onClose={() => setPreviewPhoto(null)} />
      )}
    </>,
    document.body,
  );
}
